import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Flex, HStack, Text } from '@bonusly/design-system';

import { NominatedGroup } from '../../../schemas/nominations';
import { getUserTag, User } from '../../../schemas/users';
import { GroupAvatar } from './group-avatar';

// Trigger and option row components shared by the user and group nominee pickers in both
// `nomination-modal.tsx` (real submission flow) and `nomination-preview-modal.tsx`
// (admin preview). Trigger rows stay compact (single line, ellipsis-truncated) so the
// closed-state of `SearchableSelect` fits in a form field; option rows render the larger
// two-line layout (avatar + name + secondary identifier) for the dropdown menu.

export const UserTriggerRow: FC<{ user: User }> = ({ user }) => (
	<HStack gap={2} minW={0}>
		<Avatar size="sm" name={user.displayName} src={user.profilePic} />
		<Text fontSize="sm" fontWeight="medium" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
			{user.displayName}
		</Text>
	</HStack>
);

export const UserOptionRow: FC<{ user: User }> = ({ user }) => (
	<Flex alignItems="center" px={2} py={2}>
		<Avatar name={user.displayName} mr={2} size="sm" src={user.profilePic} />
		<Box flex={1} minWidth={0} textAlign="left">
			<Text fontSize="sm" fontWeight="medium" lineHeight={5} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
				{user.displayName}
			</Text>
			<Text color="text.softer" fontSize="xs" lineHeight={4} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
				{getUserTag(user.username)}
			</Text>
		</Box>
	</Flex>
);

export const GroupTriggerRow: FC<{ group: NominatedGroup }> = ({ group }) => (
	<HStack gap={2} minW={0}>
		<GroupAvatar groupType={group.groupType} size="sm" />
		<Text fontSize="sm" fontWeight="medium" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
			{group.displayName}
		</Text>
	</HStack>
);

export const GroupOptionRow: FC<{ group: NominatedGroup }> = ({ group }) => {
	const { t } = useTranslation('nominations', { keyPrefix: 'modal' });
	return (
		<Flex alignItems="center" px={2} py={2}>
			<GroupAvatar groupType={group.groupType} size="sm" mr={2} />
			<Box flex={1} minWidth={0} textAlign="left">
				<Text fontSize="sm" fontWeight="medium" lineHeight={5} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{group.displayName}
				</Text>
				<Text color="text.softer" fontSize="xs" lineHeight={4} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{t('groupMemberCount', { count: group.userIds.length })}
				</Text>
			</Box>
		</Flex>
	);
};
